import { useEffect, useState, useCallback, useContext } from "react";
import ReactDOM from "react-dom";
import { FaCheckCircle, FaTimesCircle, FaTimes } from "react-icons/fa";
import { FaArrowLeftLong } from "react-icons/fa6";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./OrdersPage.css";
import SampleProducts from "./SampleProducts";
import { UserContext } from "../context/UserContext";
import { ThemeContext } from "../context/themeContext";
import { useCart } from "./CartContext";
import { IoChevronBack } from "react-icons/io5";

const OrdersPage = ({ setToggle, onClose }) => {
  const navigate = useNavigate();
  const { setCartItems } = useCart();

  const { user, loading: userLoading } = useContext(UserContext);
  const { activeTheme } = useContext(ThemeContext);

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [detailsPanel, setDetailsPanel] = useState({ open: false, order: null });
  const [searchQuery, setSearchQuery] = useState("");



  const [paymentStatusPopup, setPaymentStatusPopup] = useState({
    visible: false,
    success: false,
    message: "",
  });

  /* ================= PRODUCT DETAILS ================= */
  const getProductDetails = (productId) => {
    return SampleProducts.find(
      (p) => p.id.toString() === productId.toString()
    );
  };

  const getProductDetailsByName = (productName) => {
    return SampleProducts.find(
      (p) => p.name?.toLowerCase() === productName?.toLowerCase()
    );
  };

  /* ================= FETCH ORDERS ================= */
  const fetchOrders = useCallback(async (userId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/order/order-list/${userId}`
      );
      const data = await res.json();
      if (data.success) setOrders(data.orders || []);
      else setOrders([]);
    } catch (err) {
      console.error("Error loading orders:", err);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  useEffect(() => {
    if (!userLoading && user?.user_id) {
      fetchOrders(user.user_id);
    } else if (!userLoading && !user) {
      setLoadingOrders(false);
    }
  }, [user, userLoading, fetchOrders]);

  /* ================= NORMALIZE ITEMS ================= */
  const normalizeItems = (order) => {
    const raw = Array.isArray(order.items)
      ? order.items
      : typeof order.items === "string"
      ? (() => { try { return JSON.parse(order.items); } catch { return []; } })()
      : [];

    return raw.length
      ? raw.map((item) => ({
          name: item?.name || "Product",
          quantity: item?.quantity || 1,
          product_id: item?.product_id,
        }))
      : [{
          name: order.product_name || "Product",
          quantity: order.quantity || 1,
          product_id: order.product_id,
        }];
  };

  /* ================= FILTER BY SEARCH ================= */
  const filteredOrders = orders.filter((order) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const items = normalizeItems(order);
    return (
      order.razorpay_order_id?.toLowerCase().includes(q) ||
      order.payment_id?.toLowerCase().includes(q) ||
      items.some((i) => i.name?.toLowerCase().includes(q))
    );
  });

  /* ================= PAGINATION ================= */
  const totalItems = filteredOrders.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);


  /* ================= BUY AGAIN ================= */
  const buyAgainPayment = (order) => {
    setToggle(false);
    onClose();

    const normalizedSourceItems = normalizeItems(order);
    const rebuiltCart = normalizedSourceItems
      .map((item) => {
        const matchedProduct =
          (item?.product_id && getProductDetails(item.product_id)) ||
          getProductDetailsByName(item?.name) ||
          null;
        if (!matchedProduct) return null;
        return { ...matchedProduct, quantity: Number(item?.quantity) || 1 };
      })
      .filter(Boolean);

    if (!rebuiltCart.length) {
      alert("Product details not found.");
      return;
    }

    setCartItems(rebuiltCart);
    navigate(`/cart`);
  };

  /* ================= GET PRODUCT IMAGE ================= */
  const getItemImage = (item) => {
    const itemProduct =
      (item.product_id && getProductDetails(item.product_id)) ||
      getProductDetailsByName(item.name);
    const img = itemProduct?.image?.startsWith("/")
      ? itemProduct.image
      : `/${itemProduct?.image || "image/logo.png"}`;
    const isDefault = /\/image\/logo\.png$/i.test(img);
    return activeTheme === "dark" && isDefault ? "/image/logo-dark.png" : img;
  };

  /* ================= CLOSE PANEL ================= */
  const closePanel = () => setDetailsPanel({ open: false, order: null });

  /* ================= LOCK BODY SCROLL WHEN PANEL OPEN ================= */
  useEffect(() => {
    if (detailsPanel.open) {
      // Save original styles
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      // Force body to not clip the panel
      document.body.style.overflow = "visible";
      document.body.style.position = "relative";
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
      };
    }
  }, [detailsPanel.open]);

  /* ================= PAYMENT POPUP ================= */
  const PaymentStatusPopup = () => {
    if (!paymentStatusPopup.visible) return null;
    return (
      <div className="popup-orders-container">
        <div className="popup-content">
          <button className="op-panel-close" onClick={closePanel}>
            <FaTimes />
          </button>
          {paymentStatusPopup.success ? (
            <FaCheckCircle size={50} color="#28a745" />
          ) : (
            <FaTimesCircle size={50} color="#dc3545" />
          )}
          <h3 className="popup-title">
            {paymentStatusPopup.success
              ? "Order Placed Successfully"
              : "Payment Action Complete"}
          </h3>
          <p className="popup-message">{paymentStatusPopup.message}</p>
        </div>
      </div>
    );
  };

  /* ================= ORDER DETAILS SIDE PANEL ================= */
  const OrderDetailsPanel = () => {
    const { open, order } = detailsPanel;
    if (!order) return null;

    const items = normalizeItems(order);
    const total = parseFloat(order.amount || 0).toFixed(2);

    const panelContent = (
      <>
        {/* Backdrop */}
        <div
          className={`op-backdrop ${open ? "op-backdrop--visible" : ""}`}
          onClick={closePanel}
        />

        {/* Panel */}
        <div className={`op-panel ${open ? "op-panel--open" : ""}`}>

          {/* HEADER — fixed inside panel */}
          <div className="op-panel-header">
            {/* ← back = close panel, go back to orders list */}
            <button className="op-panel-back" onClick={closePanel}>
  <IoChevronBack className="back-icon mobile-back" />
  <FaArrowLeftLong className="desktop-back" />
</button>
            <div className="op-panel-header-center">
              <h2 className="op-panel-title">ORDER DETAILS</h2>
              <span className="op-panel-subtitle">{order.razorpay_order_id}</span>
            </div>
            {/* X = close entire orders page */}
           <button
  className="op-panel-close"
  onClick={() => {
    closePanel();
    setToggle(false);
    onClose();
    navigate("/dashboard");
  }}
>
  <FaTimes />
</button>
          </div>

          {/* BODY — contains scrollable items + sticky footer */}
          <div className="op-panel-body">

            {/* ITEMS — scrollable */}
            <div className="op-panel-items">
              {items.map((item, idx) => {
                const img = getItemImage(item);
                const itemProduct =
                  (item.product_id && getProductDetails(item.product_id)) ||
                  getProductDetailsByName(item.name);
                const price = itemProduct?.price
                  ? `₹${(parseFloat(itemProduct.price) * item.quantity).toFixed(2)}`
                  : null;

                return (
                  <div className="op-panel-item" key={idx}>
                    <img src={img} alt={item.name} className="op-panel-item-img" />
                    <span className="op-panel-item-num">{idx + 1}.</span>
                    <span className="op-panel-item-name">{item.name}</span>
                    {price && (
                      <span className="op-panel-item-price">
                        <span className="op-total-label">TOTAL </span>
                        {price}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* FOOTER — sticky at bottom of body */}
            <div className="op-panel-footer">
              <span className="op-footer-total">
                <span className="op-total-label">TOTAL </span>
                ₹{total}
              </span>
              <button
                className="op-buy-again-btn"
                onClick={() => {
                  closePanel();
                  buyAgainPayment(order);
                }}
              >
                Buy Again
              </button>
            </div>

          </div>
        </div>
      </>
    );

    return ReactDOM.createPortal(panelContent, document.body);
  };

  /* ================= LOADING ================= */
  if (userLoading || loadingOrders) {
    return (
      <div className="orders-main-container">
        <p style={{ textAlign: "center", marginTop: "40px" }}>Loading orders...</p>
      </div>
    );
  }

  // ==========================================
  //               UI
  // ==========================================
  return (
    <div className="orders-main-container">

      {/* HEADING */}
      <div className="orders-heading-container">
        <button
          className="back-btn-orders"
          onClick={() => { setToggle(false); onClose(); navigate("/dashboard"); }}
        >
          <FaArrowLeftLong size={22} />
        </button>
        <h2 className="report-heading">Your Orders</h2>
      </div>

      {/* PAGINATION BAR — above search */}
      <div className="orders-pagination-bar">
        <div className="pagination-info">
          {totalItems === 0
            ? "0 items"
            : `${startIndex + 1}–${Math.min(endIndex, totalItems)} of ${totalItems} items`}
        </div>

        <div className="pagination-controls-wrap">
          {/* Prev / Page number / Next */}
          {totalPages > 1 && (
            <div className="pagination-nav">
              {currentPage > 1 && (
                <button className="pagination-nav-btn" onClick={() => setCurrentPage(p => p - 1)}>&lt;</button>
              )}
              <span className="pagination-current">{currentPage}</span>
              {currentPage < totalPages && (
                <button className="pagination-nav-btn" onClick={() => setCurrentPage(p => p + 1)}>&gt;</button>
              )}
            </div>
          )}

          {/* Items per page dropdown */}
          <select
            className="pagination-perpage-select"
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>{n} / Page</option>
            ))}
          </select>
        </div>
      </div>

      {/* SEARCH */}
      <div className="orders-search-bar">
        <span className="orders-search-icon"><FaSearch /></span>
        <input
          type="text"
          className="orders-search-input"
          placeholder="Search orders..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        />
      </div>

      {/* ORDERS LIST */}
      <div className="orders-list-container">
        {paginatedOrders.length === 0 ? (
          <p style={{ textAlign: "center", marginTop: "40px" }}>No orders found.</p>
        ) : (
          paginatedOrders.map((order, i) => {
            const items = normalizeItems(order);
            const itemCount = items.length;

            return (
              <div className="order-row-card" key={i}>
                <div className="order-row-left">
                  <div className="order-row-id">
                    <strong>ORDER ID:</strong> #{order.razorpay_order_id}
                  </div>
                  {order.payment_id && (
                    <div className="order-row-payment-id">
                      <strong>PAYMENT ID:</strong> #{order.payment_id}
                    </div>
                  )}
                  <div className="order-row-date">
                     Order Placed: {new Date(order.created_at).toLocaleString('en-IN', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true
}).replace(',' , '  ').replace(/\b(am|pm)\b/i, (match) => match.toUpperCase())
}
                  </div>
                </div>

                <button
                  className="order-view-btn"
                  onClick={() => setDetailsPanel({ open: true, order })}
                >
                  VIEW ALL ITEMS ({itemCount})
                </button>
              </div>
            );
          })
        )}
      </div>

      <OrderDetailsPanel />
      <PaymentStatusPopup />
    </div>
  );
};

export default OrdersPage;
